import React from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Alert, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { StoreUser } from '../../../../services/authService';
import { adminButtonSx, adminButtonToneSx, adminCombineSx, adminFormControlSx } from '../../../../styles/adminComponentStyles';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';

type StoreOnboardingEmailActionsProps = {
    onboardingEmailRecipientStoreUserId: string;
    onboardingEmailContacts: StoreUser[];
    onboardingEmailSending: boolean;
    onboardingEmailResult: { type: 'success' | 'error'; text: string } | null;
    onSelectEmailRecipient: (value: string) => void;
    onSendOnboardingEmail: () => void;
};

const StoreOnboardingEmailActions: React.FC<StoreOnboardingEmailActionsProps> = ({
    onboardingEmailRecipientStoreUserId,
    onboardingEmailContacts,
    onboardingEmailSending,
    onboardingEmailResult,
    onSelectEmailRecipient,
    onSendOnboardingEmail,
}) => (
    <>
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
        >
            <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text, fontSize: '20px' }}>
                Onboarding Status
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl
                    size="small"
                    sx={adminCombineSx(adminFormControlSx, {
                        minWidth: { sm: 260 },
                        '& .MuiSelect-icon': { color: adminColors.mutedText, right: 8, zIndex: 1 },
                        '& .MuiSelect-select': {
                            overflow: 'hidden',
                            paddingRight: '36px !important',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                    })}
                >
                    <InputLabel id="onboarding-email-contact-label">Contact</InputLabel>
                    <Select
                        labelId="onboarding-email-contact-label"
                        label="Contact"
                        value={onboardingEmailRecipientStoreUserId}
                        onChange={(event) => onSelectEmailRecipient(event.target.value as string)}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                    >
                        {onboardingEmailContacts.map((contact) => (
                            <MenuItem key={contact.id} value={contact.id}>
                                {contact.display_name} ({contact.role}) - {contact.contact_email}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    size="small"
                    variant="contained"
                    onClick={onSendOnboardingEmail}
                    disabled={!onboardingEmailRecipientStoreUserId || onboardingEmailSending}
                    sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, {
                        height: '36px !important',
                        minHeight: '36px !important',
                        whiteSpace: 'nowrap',
                    })}
                >
                    {onboardingEmailSending ? 'Sending…' : 'Email Status'}
                </Button>
            </Stack>
        </Stack>
        {onboardingEmailResult && (
            <Alert severity={onboardingEmailResult.type} sx={{ mb: 1.5, borderRadius: '8px', fontSize: 13 }}>
                {onboardingEmailResult.text}
            </Alert>
        )}
    </>
);

export default React.memo(StoreOnboardingEmailActions);
